import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const loadUserSession = () => {
    try {
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user") || "null");
        if (token && user) return { user, token, isSubscribed: user.subscribed || false };
    } catch {}
    return { user: null, token: null, isSubscribed: false };
};

export const loginUser = createAsyncThunk("user/login", async ({ email, password }) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    // Evict any vendor session
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("vendor_data");
    window.dispatchEvent(new Event("vendor-logout"));
    return data; // { user, token }
});

export const registerUser = createAsyncThunk("user/register", async (userData) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Registration failed");
    // Evict any vendor session
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("vendor_data");
    window.dispatchEvent(new Event("vendor-logout"));
    return data; // { user, token }
});

export const fetchUserRedemptions = createAsyncThunk(
    "user/fetchRedemptions",
    async ({ userId, token }) => {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/coupon-redemptions/user/${userId}`, { headers });
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    }
);

export const fetchUserById = createAsyncThunk("user/fetchById", async ({ userId, token }) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return await res.json();
});

export const fetchUserAnalytics = createAsyncThunk("user/fetchAnalytics", async ({ userId, token }) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userId}/analytics`, { headers });
    return await res.json();
});

const { user: storedUser, token: storedToken, isSubscribed: storedSubscribed } = loadUserSession();

const initialState = {
    user: storedUser,
    token: storedToken,
    isSubscribed: storedSubscribed,
    subscriptionStartDate: null,
    status: "idle",
    error: null,
    redemptions: [],
    redemptionsStatus: "idle",
    analytics: null,
    analyticsStatus: "idle",
};

const applyAuthPayload = (state, { user, token }) => {
    state.user = user;
    state.token = token;
    state.isSubscribed = user?.subscribed || false;
    state.status = "succeeded";
    state.error = null;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
};

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        setUser: (state, action) => {
            applyAuthPayload(state, action.payload);
        },
        clearUser: (state) => {
            state.user = null;
            state.token = null;
            state.isSubscribed = false;
            state.subscriptionStartDate = null;
            state.status = "idle";
            state.error = null;
            localStorage.removeItem("token");
            localStorage.removeItem("user");
        },
        updateUser: (state, action) => {
            state.user = { ...state.user, ...action.payload };
            state.isSubscribed = state.user?.subscribed || false;
            localStorage.setItem("user", JSON.stringify(state.user));
        },
        setSubscribed: (state, action) => {
            state.isSubscribed = action.payload.isSubscribed;
            state.subscriptionStartDate = action.payload.subscriptionStartDate ?? null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                applyAuthPayload(state, action.payload);
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(registerUser.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                applyAuthPayload(state, action.payload);
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchUserById.fulfilled, (state, action) => {
                state.user = { ...state.user, ...action.payload };
                localStorage.setItem("user", JSON.stringify(state.user));
            })
            .addCase(fetchUserRedemptions.pending, (state) => {
                state.redemptionsStatus = "loading";
            })
            .addCase(fetchUserRedemptions.fulfilled, (state, action) => {
                state.redemptions = action.payload;
                state.redemptionsStatus = "succeeded";
            })
            .addCase(fetchUserRedemptions.rejected, (state) => {
                state.redemptionsStatus = "failed";
            })
            .addCase(fetchUserAnalytics.pending, (state) => {
                state.analyticsStatus = "loading";
            })
            .addCase(fetchUserAnalytics.fulfilled, (state, action) => {
                state.analytics = action.payload;
                state.analyticsStatus = "succeeded";
            })
            .addCase(fetchUserAnalytics.rejected, (state) => {
                state.analyticsStatus = "failed";
            });
    },
});

export const { setUser, clearUser, updateUser, setSubscribed } = userSlice.actions;
export default userSlice.reducer;
